import Swal from "sweetalert2";

export const STAGE_RULES_KEYS = {
  1: "downandacross_rules",
  2: "wordpuzzle_rules",
  3: "unscramble_rules",
  4: "quiz_rules",
  5: "dos_dont_rules",
};

export const DEFAULT_STAGE_RULES = {
  downandacross_rules: [
    "Fill each square with one letter using the Across and Down clues.",
    "Tap a clue to highlight that word on the grid.",
    "Earn points when you complete a word.",
  ],
  wordpuzzle_rules: [
    "Click and drag across letters to select a word.",
    "Words may run horizontally, vertically, or diagonally.",
    "Find every word in the list to continue.",
  ],
  unscramble_rules: [
    "Type each letter in the tiles to unscramble the word.",
    "A green check appears when a row is correct.",
    "Submit after solving the minimum number of words.",
  ],
  quiz_rules: [
    "Read each question and choose the option that best reflects your perspective.",
    "Answer all questions to continue to the next stage.",
  ],
  dos_dont_rules: [
    "Drag each statement from the pool into the DO'S or DON'TS column.",
    "Place every statement, then tap Submit to finish the game.",
  ],
};

export const STAGE_RULES_LABELS = {
  downandacross_rules: "Stage 1 — Down and Across",
  wordpuzzle_rules: "Stage 2 — Word Puzzle",
  unscramble_rules: "Stage 3 — Unscramble",
  quiz_rules: "Stage 4 — Quiz",
  dos_dont_rules: "Stage 5 — Do's and Don'ts",
};

export function ensureRulesArray(val) {
  if (Array.isArray(val)) {
    return val.map((r) => String(r ?? "").trim()).filter(Boolean);
  }
  if (val == null || val === "") return [];
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) {
        return parsed.map((r) => String(r ?? "").trim()).filter(Boolean);
      }
    } catch {
      const t = val.trim();
      return t ? [t] : [];
    }
    const t = val.trim();
    return t ? [t] : [];
  }
  return [];
}

export function getStageRulesFromTheme(themeData, rulesKey) {
  const configured = ensureRulesArray(themeData?.[rulesKey]);
  if (configured.length) return configured;

  if (rulesKey === "downandacross_rules") {
    const legacy = ensureRulesArray(themeData?.rules_text ?? themeData?.rules);
    if (legacy.length) return legacy;
  }

  return [...(DEFAULT_STAGE_RULES[rulesKey] || [])];
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function formatRulesAsHelpHtml(rules, title = "How to play") {
  const list = ensureRulesArray(rules);
  const steps = list.length
    ? list
        .map(
          (rule, i) => `<li class="stage-help-step">
            <span class="stage-help-step__num" aria-hidden="true">${i + 1}</span>
            <span class="stage-help-step__text">${escapeHtml(rule)}</span>
          </li>`
        )
        .join("")
    : `<li class="stage-help-step">
        <span class="stage-help-step__num" aria-hidden="true">1</span>
        <span class="stage-help-step__text">No rules configured for this stage.</span>
      </li>`;

  return `<div class="stage-help-card">
    <div class="stage-help-card__header">
      <h2 class="stage-help-card__title">${escapeHtml(title)}</h2>
    </div>
    <ol class="stage-help-steps">${steps}</ol>
    <button type="button" class="stage-help-got-it-btn" data-stage-help-close>Got it!</button>
  </div>`;
}

const STAGE_HELP_MODAL_CLASS = "stage-help-modal-open";
const STAGE_HELP_BACKDROP_ID = "stage-help-backdrop";

let helpModalScrollY = 0;
let stageModalLockCount = 0;

function mountStageHelpBackdrop() {
  if (document.getElementById(STAGE_HELP_BACKDROP_ID)) return;
  const backdrop = document.createElement("div");
  backdrop.id = STAGE_HELP_BACKDROP_ID;
  backdrop.className = "stage-help-backdrop";
  backdrop.setAttribute("aria-hidden", "true");
  document.body.appendChild(backdrop);
}

function unmountStageHelpBackdrop() {
  document.getElementById(STAGE_HELP_BACKDROP_ID)?.remove();
}

export function lockPageScrollForStageModal() {
  if (stageModalLockCount === 0) {
    helpModalScrollY =
      window.scrollY || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add(STAGE_HELP_MODAL_CLASS);
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${helpModalScrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    mountStageHelpBackdrop();
  }
  stageModalLockCount += 1;
}

export function unlockPageScrollForStageModal() {
  if (stageModalLockCount <= 0) return;
  stageModalLockCount -= 1;
  if (stageModalLockCount > 0) return;

  document.documentElement.classList.remove(STAGE_HELP_MODAL_CLASS);
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
  document.body.style.position = "";
  document.body.style.top = "";
  document.body.style.left = "";
  document.body.style.right = "";
  document.body.style.width = "";
  unmountStageHelpBackdrop();
  window.scrollTo(0, helpModalScrollY);
}

export function fireStageHelpSwal({
  rules,
  title = "How to play",
  confirmButtonColor = "#39b467",
}) {
  lockPageScrollForStageModal();

  Swal.fire({
    html: formatRulesAsHelpHtml(rules, title),
    showConfirmButton: false,
    showCloseButton: false,
    position: "center",
    width: "min(96vw, 44rem)",
    heightAuto: true,
    scrollbarPadding: false,
    returnFocus: false,
    customClass: {
      popup: "stage-help-popup",
      htmlContainer: "stage-help-popup__html",
    },
    showClass: { popup: "stage-help-popup--in" },
    didOpen: () => {
      const btn = Swal.getPopup()?.querySelector("[data-stage-help-close]");
      if (btn) {
        btn.style.background = confirmButtonColor || "#39b467";
        btn.addEventListener("click", () => Swal.close(), { once: true });
      }
    },
    didClose: () => {
      unlockPageScrollForStageModal();
    },
  });
}

export function getAllStageRulesSections(themeData) {
  return Object.entries(STAGE_RULES_KEYS).map(([, key]) => ({
    key,
    label: STAGE_RULES_LABELS[key] || key,
    rules: getStageRulesFromTheme(themeData, key),
  }));
}
